export const INVOICING_BLOCKED_ROLES = [
  'Site Engineer',
  'Senior Site Engineer',
  'Site Supervisor',
  'Technician',
  'Backoffice Engineer'
];

export const SUPER_ADMIN_ROLE = 'Super Admin';

export const PERMISSION_MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'projects', label: 'Projects' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'shop_drawings', label: 'Shop Drawings' },
  { key: 'files', label: 'Files' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'procurement', label: 'Procurement' },
  { key: 'forecast', label: 'Forecast' },
  { key: 'invoicing', label: 'Invoicing' },
  { key: 'stages', label: 'Stages' },
  { key: 'material', label: 'Material' },
  { key: 'project_structure', label: 'Project Structure' },
  { key: 'execution', label: 'Execution' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'variations', label: 'Variations' },
  { key: 'admin', label: 'Admin' },
  { key: 'ai', label: 'AI' },
  { key: 'stage_lpo_loi', label: 'LPO/LOI stage' },
];

// Roles that see the LPO/LOI stage when the user object carries no permission list (older sessions);
// with a list, the `stage_lpo_loi` module decides (Admin › Access). The backend enforces it either way.
export const LPO_LOI_DEFAULT_ROLES = ['Admin', 'Presales'];

export const hasPermission = (userOrRole, module) => {
  if (!userOrRole || !module) return false;
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole.role;
  if (role === SUPER_ADMIN_ROLE) return true;
  const permissions = typeof userOrRole === 'object' && Array.isArray(userOrRole.permissions)
    ? userOrRole.permissions
    : null;
  if (permissions) {
    if (module === 'shop_drawings' && permissions.includes('tasks')) return true;
    return permissions.includes(module);
  }
  if (module === 'ai') return false;
  if (module === 'admin') return role === 'Admin';
  if (module === 'stage_lpo_loi') return LPO_LOI_DEFAULT_ROLES.includes(role);
  if (module === 'invoicing') return !INVOICING_BLOCKED_ROLES.includes(role);
  if (module === 'variations') return role !== 'Site Engineer';
  return true;
};

export const canAccessInvoicing = (userOrRole) => hasPermission(
  typeof userOrRole === 'string' ? { role: userOrRole } : userOrRole,
  'invoicing'
);

/*
  View / Edit levels (Admin › Access, wave 3 · 28 #8). A role may open a module but only VIEW it; the server
  refuses its writes there (permissions.ensure_module_editable). The signed-in user carries the list as
  `view_only_modules` (sign-in and the session refresh return it, /auth/me too). A level changed while someone is
  signed in reaches their screen with their next sign-in or refresh; a session saved before the list existed has
  none, so everything its role opens counts as Edit here until then — the server still says no either way. Viewer
  accounts are read-only everywhere.
*/
export const READ_ONLY_ROLES = ['Viewer'];

export const canEdit = (userOrRole, module) => {
  if (!hasPermission(userOrRole, module)) return false;
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole?.role;
  if (role === SUPER_ADMIN_ROLE) return true;
  if (READ_ONLY_ROLES.includes(role)) return false;
  const viewOnly = typeof userOrRole === 'object' && Array.isArray(userOrRole?.view_only_modules) ? userOrRole.view_only_modules : [];
  const permissions = typeof userOrRole === 'object' && Array.isArray(userOrRole?.permissions) ? userOrRole.permissions : null;
  // Shop Drawings reached through Tasks follows the Tasks level (same rule as the server)
  const target = module === 'shop_drawings' && permissions && !permissions.includes('shop_drawings') && permissions.includes('tasks') ? 'tasks' : module;
  return !viewOnly.includes(target);
};

/*
  The app pages where people make changes (28 #8). The server refuses every change a View-only role tries on all of
  them; `hidesEditButtons` marks the pages that also hide their create / edit / delete controls from such a role (and
  from Viewer accounts) with canEdit() — on a page without it the buttons would still show and a click would answer
  "… is view-only for your role". Every page listed here hides them now: Tasks (board, quick add, task pane, bulk
  bars, row menus, drag) and Shop Drawings (Add drawing, import, status / row menus, bulk bars, reply forms) since
  BUG-002. Admin › Access words its View · Edit note from this list: a new page joins with `hidesEditButtons: false`
  until it gates its buttons.
*/
export const EDITABLE_PAGES = [
  { key: 'projects', label: 'Projects', modules: ['projects'], hidesEditButtons: true },
  { key: 'tasks', label: 'Tasks', modules: ['tasks'], hidesEditButtons: true },
  { key: 'shop_drawings', label: 'Shop Drawings', modules: ['shop_drawings'], hidesEditButtons: true },
  { key: 'documents', label: 'Documents', modules: ['files'], hidesEditButtons: true },
  { key: 'inventory', label: 'Inventory', modules: ['inventory'], hidesEditButtons: true },
  { key: 'procurement', label: 'Procurement', modules: ['procurement', 'material'], hidesEditButtons: true },
  { key: 'forecast', label: 'Forecast', modules: ['procurement'], hidesEditButtons: true },
  { key: 'invoicing', label: 'Invoicing', modules: ['invoicing'], hidesEditButtons: true },
];
